// Chakra Imports
import {
  Avatar,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom Components
import { ItemContent } from "components/menu/ItemContent";
import { SidebarResponsive } from "components/sidebar/Sidebar";
import PropTypes from "prop-types";
// Assets
import { MdNotificationsActive, MdNotificationsNone, MdTune } from "react-icons/md";
import { GiNothingToSay } from "react-icons/gi";
import routes from "routes";
import { Link } from "react-router-dom";
import { useAuth } from "contexts/AuthProvider";
import { useEffect, useState } from "react";
// API
import { ApiNotificationList } from "api/notifications/notifications";
import { Notification } from "api/types";
import { useSimulatorPremisesDrawerOptional } from "contexts/SimulatorPremisesDrawerContext";

export default function HeaderLinks(props: { secondary: boolean }) {
  const { user, userActions, logoutAction } = useAuth();
  const { secondary } = props;
  const simulatorPremisesDrawer = useSimulatorPremisesDrawerOptional();
  const showSimulatorListingNav =
    !simulatorPremisesDrawer || simulatorPremisesDrawer.listingMode;

  // Chakra Color Mode
  const navbarIcon = useColorModeValue("gray.400", "white");
  let menuBg = useColorModeValue("white", "navy.800");

  const [notifications, setNotifications] = useState([])
  const [newNotifications, setNewNotifications] = useState(0)
  const [lastReview, setLastReview] = useState(new Date(userActions.notifications.value))

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.18)",
    "14px 17px 40px 4px rgba(112, 144, 176, 0.06)"
  );

  function fetchNotifications(d: Date) {
    ApiNotificationList().then((response) => {
      let res: any[] = response.data
      setNotifications(
        res.map((notification) => {
          let object: Notification = {
            id: notification.id,
            hasAttachment: notification.has_attachment,
            sender: notification.sender,
            title: notification.title,
            description: notification.description,
            createdAt: new Date(notification.created_at),
            icon: notification.icon,
            downloadableFileName: notification.downloadable_filename
          }
          return (object)
        }
        ))
      setNewNotifications(notifications.filter((n) => n.datetime >= d).length)
    })
  }

  useEffect(() => {
    userActions.notifications.change(lastReview)
    fetchNotifications(lastReview)
    const interval = setInterval(() => fetchNotifications(lastReview), 300_000)
    return () => clearInterval(interval)
  }, [lastReview])

  return (
    <Flex
      w={{ sm: "100%", md: "auto" }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap={secondary ? { base: "wrap", md: "nowrap" } : "unset"}
      p="10px"
      borderRadius="30px"
      boxShadow={shadow}
    >
      {showSimulatorListingNav ? (
        <SidebarResponsive
          routes={routes}
          showOnDesktop={Boolean(simulatorPremisesDrawer)}
        />
      ) : null}
      {simulatorPremisesDrawer && showSimulatorListingNav ? (
        <IconButton
          aria-label="Abrir premisas y filtros de cobertura"
          icon={<MdTune />}
          variant="ghost"
          size="sm"
          onClick={simulatorPremisesDrawer.onOpen}
          color={navbarIcon}
          me="6px"
          borderRadius="full"
        />
      ) : null}
      <Menu>
        <MenuButton p='0px' onClick={(ev) => { setLastReview(new Date()) }}>
          <Icon mt='6px' as={newNotifications === 0 ? MdNotificationsNone : MdNotificationsActive} color={newNotifications === 0 ? navbarIcon : 'blue.200'} w='18px' h='18px' me='10px' />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p='20px'
          borderRadius='20px'
          bg={menuBg}
          mt='22px'
          me={{ base: '30px', md: 'unset' }}
          w={{ base: '360px', md: '500px', '2xl': '600px' }}>
          <Flex w='100%' mb='20px'>
            <Text fontSize='md' fontWeight='600' color={textColor}>
              {'Notificaciones ' + '(' + notifications.length + ')'}
            </Text>
          </Flex>
          <Flex flexDirection='column' maxH='290px' overflow='scroll'>
            {notifications.length > 0 ? notifications.map((n) => <ItemContent notification={n} />) : (
              <>
                <Icon as={GiNothingToSay} alignSelf={"center"} height={'80px'} w={'50px'} />
                <Text alignSelf={"center"} pb='3rem'>Aún no hay notificaciones.</Text>
              </>
            )}
          </Flex>
        </MenuList>
      </Menu>

      <Menu>
        <MenuButton p="0px">
          <Avatar
            _hover={{ cursor: "pointer" }}
            color="white"
            name={`${user.first_name} ${user.last_name}`}
            bg="#11047A"
            size="sm"
            w="40px"
            h="40px"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="0px"
          mt="10px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
        >
          <Flex w="100%" mb="0px">
            <Text
              ps="20px"
              pt="16px"
              pb="10px"
              w="100%"
              borderBottom="1px solid"
              borderColor={borderColor}
              fontSize="sm"
              fontWeight="700"
              color={textColor}
            >
              👋&nbsp; Hey, {user.first_name}
            </Text>
          </Flex>
          <Flex flexDirection="column" p="10px">
            <MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px'>
              <Text fontSize='sm'>{user.department.name}</Text>
            </MenuItem>
            {user.is_staff ?
              <>
                <Link to={process.env.REACT_APP_BASE_URL + '/api/schema/swagger-ui'} target="_blank">
                  <MenuItem color="gray.400" _hover={{ bg: '#0b1437', color: 'white' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px'>
                    <Text fontSize='sm'>Swagger</Text>
                  </MenuItem>
                </Link>
                <Link to={process.env.REACT_APP_BASE_URL + '/admin'} target="_blank">
                  <MenuItem color="gray.400" _hover={{ bg: '#0b1437', color: 'white' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px'>
                    <Text fontSize='sm'>Página admin</Text>
                  </MenuItem>
                </Link>
              </>
              : null}
            <MenuItem
              _hover={{ color: 'red.600', bg: '#0b1437' }}
              _focus={{ bg: "none" }}
              color="red.400"
              borderRadius="8px"
              px="14px"
              onClick={() => logoutAction()}
            >
              <Text fontSize="sm">
                Cerrar Sesión
              </Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
